import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowsClockwise, Sparkle } from "@phosphor-icons/react";
import { useI18n } from "../lib/i18n";
import { recreateCarousel } from "../lib/api";
import { toast } from "sonner";

export default function Recreate() {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [text, setText] = useState("");
  const [slides, setSlides] = useState(10);
  const [tone, setTone] = useState("guide");
  const [boost, setBoost] = useState(true);
  const [loading, setLoading] = useState(false);

  const tones = ["trailblazer", "guide", "maverick", "elite_ceo", "analytical", "coach"];
  const toneLabel = (k) => t(`tone_${k}`);

  const submit = async () => {
    if (text.trim().length < 20) {
      toast.error(lang === "el" ? "Προσθέσε μεγαλύτερο κείμενο" : "Please paste more text");
      return;
    }
    setLoading(true);
    try {
      const data = await recreateCarousel({
        original_text: text,
        slides_count: slides,
        language: lang,
        tone,
        conversion_boost: boost,
      });
      toast.success(lang === "el" ? "Έτοιμο!" : "Ready!");
      nav(`/editor/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-6 md:px-10 pt-12 pb-24 max-w-3xl mx-auto" data-testid="recreate-page">
      <div className="mb-10">
        <div className="kicker-label mb-3 flex items-center gap-2"><ArrowsClockwise size={14} weight="duotone" /> {t("nav_recreate")}</div>
        <h1 className="font-heading font-semibold text-[40px] md:text-[56px] leading-[0.95] tracking-tight text-ink">
          {t("recreate_title")}
        </h1>
        <p className="mt-4 text-ink-soft text-lg max-w-xl">{t("recreate_sub")}</p>
      </div>

      <textarea
        rows={10}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={t("paste_here")}
        className="cs-input w-full"
        data-testid="recreate-text"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
        <div>
          <div className="kicker-label mb-2">{t("step2_slides")}: <span className="text-forest font-semibold">{slides}</span></div>
          <input
            type="range"
            min={3}
            max={20}
            value={slides}
            onChange={(e) => setSlides(parseInt(e.target.value))}
            className="w-full accent-[#244A36]"
            data-testid="recreate-slides"
          />
        </div>
        <div>
          <div className="kicker-label mb-2">Tone</div>
          <div className="flex flex-wrap gap-2" data-testid="recreate-tone-group">
            {tones.map((k) => (
              <button key={k} onClick={() => setTone(k)} className={`chip ${tone === k ? "is-active" : ""} px-3 py-1.5 rounded-full text-xs`} data-testid={`recreate-tone-${k}`}>
                {toneLabel(k)}
              </button>
            ))}
          </div>
        </div>
      </div>

      <label className="mt-6 flex items-center justify-between p-4 rounded-2xl border border-line cursor-pointer" data-testid="recreate-boost">
        <div>
          <div className="font-heading font-medium text-ink">{t("step2_boost")}</div>
          <div className="text-sm text-ink-soft">{t("step2_boost_desc")}</div>
        </div>
        <input type="checkbox" checked={boost} onChange={(e) => setBoost(e.target.checked)} className="w-5 h-5 accent-[#244A36]" />
      </label>

      <div className="mt-10 flex justify-end">
        <button
          onClick={submit}
          disabled={loading}
          className="inline-flex items-center gap-2 bg-forest text-cream rounded-full px-7 py-3.5 text-sm font-medium hover:bg-forest-dark transition-all hover:-translate-y-0.5 disabled:opacity-60"
          data-testid="recreate-submit"
        >
          <Sparkle weight="duotone" size={16} />
          {loading ? t("generating") : t("recreate_cta")}
        </button>
      </div>
    </div>
  );
}
